import { type JSX } from 'react';
import { Link } from 'react-router-dom';
import { Seo } from '@/components/seo';
import { PublicPageHeading } from '@/components/public/PublicPageHeading';
import { ContactLeadForm, DEFAULT_CONTACT_FORM_CONTENT } from '@/components/public/ContactLeadForm';
import { PublicPageLayout, PublicPageSection } from '@/layouts/PublicPageLayout';
import { buildContactInfo } from '@/lib/constants/contactInfo';
import { useSiteSettingsDetail } from '@/lib/query/resources/siteSettings';
import type { ContactPageContent } from '@/lib/types/siteSettings';
import { mergeContentWithDefaults } from '@/lib/utils/contentDefaults';

const DEFAULT_CONTACT_PAGE_CONTENT: ContactPageContent = {
  seo: {
    title: 'Contact Us',
    description:
      'Contact Strashin Developments for trouble-free commercial workspaces tailored to your business.',
  },
  hero: {
    eyebrow: 'Contact Us',
    title: 'Contact Us For Trouble-Free Commercial Workspaces',
    description:
      'We prioritize smooth, reliable tenant experiences so you can focus on your business. Let us help you find the right workspace.',
    responseTime: 'Response time: within 1 business day.',
  },
  heroCard: {
    eyebrow: 'Quick contact',
    title: 'Talk to our leasing team',
    description: 'Share your timeline and space needs. We respond within 1 business day.',
    callLabel: 'Call now',
    emailLabel: 'Email us',
    mapLabel: 'Open in maps',
  },
  body: {
    title: 'What happens next',
    description:
      'Share a few details and we will confirm availability, match you with best-fit spaces, and coordinate next steps.',
    highlights: [
      'Shortlist of suites matched to size, use, and timing.',
      'Tour coordination and tenant support.',
      'Clear next steps on pricing, incentives, and fit-up options.',
    ],
    responseTimeLabel: 'Response time',
    responseTimeValue: 'Within 1 business day',
    directionsLabel: 'Get directions',
    availabilitiesLabel: 'View availabilities',
  },
  details: {
    title: 'Contact details',
    description: 'Reach our team directly for leasing inquiries or tenant support.',
  },
  form: DEFAULT_CONTACT_FORM_CONTENT,
};

function buildMapsLink(addressLines: string[]): string {
  const address = addressLines.filter((line) => line.trim().length > 0).join(', ');
  if (address.length === 0) {
    return 'https://maps.google.com';
  }
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
}

export function ContactUs(): JSX.Element {
  const siteSettingsQuery = useSiteSettingsDetail();
  const siteSettings = siteSettingsQuery.data ?? null;
  const contactInfo = buildContactInfo(siteSettings);
  const contactPhone = contactInfo.phone;
  const contactEmail = contactInfo.email;
  const mapsLink = buildMapsLink(contactInfo.addressLines);
  const phoneLink = `tel:${contactPhone.replace(/[^\d+]/g, '')}`;
  const emailLink = `mailto:${contactEmail}`;
  const contactPageContent = mergeContentWithDefaults(
    siteSettings?.publicContent?.contactPage,
    DEFAULT_CONTACT_PAGE_CONTENT,
  );
  const sharedContactLabels = siteSettings?.publicContent?.shared.contactSection;

  return (
    <PublicPageLayout>
      <Seo
        title={contactPageContent.seo.title}
        description={contactPageContent.seo.description}
        canonicalPath="/contact"
      />

      <PublicPageSection
        tone="contrast"
        spacing="lg"
        aria-label="Contact Strashin Developments"
        containerClassName="grid gap-xl md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] md:items-center"
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm font-semibold uppercase tracking-wide text-white/80">
            {contactPageContent.hero.eyebrow}
          </p>
          <h1 className="text-4xl font-semibold leading-tight text-white md:text-5xl">
            {contactPageContent.hero.title}
          </h1>
          <p className="max-w-3xl text-lg text-white/85">{contactPageContent.hero.description}</p>
          <p className="text-sm text-white/70">{contactPageContent.hero.responseTime}</p>
        </div>
        <div className="rounded-3xl bg-white p-6 shadow-lg">
          <p className="text-xs font-semibold uppercase tracking-wide text-brand-accent">
            {contactPageContent.heroCard.eyebrow}
          </p>
          <h2 className="mt-2 text-2xl font-semibold text-brand-primary">
            {contactPageContent.heroCard.title}
          </h2>
          <p className="mt-3 text-sm text-text-primary/70">
            {contactPageContent.heroCard.description}
          </p>
          <dl className="mt-5 grid gap-4 sm:grid-cols-2">
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                {sharedContactLabels?.phoneLabel ?? 'Phone'}
              </dt>
              <dd className="mt-2 text-base font-semibold text-brand-primary">
                <a href={phoneLink} className="hover:underline">
                  {contactPhone}
                </a>
              </dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                {sharedContactLabels?.emailLabel ?? 'Email'}
              </dt>
              <dd className="mt-2 text-base font-semibold text-brand-primary">
                <a href={emailLink} className="hover:underline">
                  {contactEmail}
                </a>
              </dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                {sharedContactLabels?.hoursLabel ?? 'Hours'}
              </dt>
              <dd className="mt-2 grid gap-2 text-sm text-text-primary/80 sm:grid-cols-2">
                {contactInfo.hours.map((entry) => (
                  <div key={`${entry.label}-${entry.value}`} className="flex flex-col">
                    <span className="font-medium text-text-primary/70">{entry.label}</span>
                    <span>{entry.value}</span>
                  </div>
                ))}
              </dd>
            </div>
          </dl>
          <div className="mt-6 flex flex-wrap gap-3">
            <a
              href={phoneLink}
              className="inline-flex min-h-11 items-center justify-center rounded-full bg-brand-primary px-5 py-2 text-sm font-semibold text-white transition hover:bg-brand-primary/90"
            >
              {contactPageContent.heroCard.callLabel}
            </a>
            <a
              href={emailLink}
              className="inline-flex min-h-11 items-center justify-center rounded-full border border-brand-primary/20 px-5 py-2 text-sm font-semibold text-brand-primary transition hover:border-brand-primary hover:bg-brand-primary/10"
            >
              {contactPageContent.heroCard.emailLabel}
            </a>
            <a
              href={mapsLink}
              target="_blank"
              rel="noreferrer"
              className="inline-flex min-h-11 items-center justify-center rounded-full border border-brand-primary/20 px-5 py-2 text-sm font-semibold text-brand-primary transition hover:border-brand-primary hover:bg-brand-primary/10"
            >
              {contactPageContent.heroCard.mapLabel}
            </a>
          </div>
        </div>
      </PublicPageSection>

      <PublicPageSection tone="base" spacing="lg">
        <div className="grid gap-8 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] lg:items-start lg:gap-10">
          <div className="flex flex-col gap-6">
            <article className="rounded-3xl border border-surface-subtle bg-white p-6 shadow-card md:p-8">
              <PublicPageHeading
                title={contactPageContent.body.title}
                description={contactPageContent.body.description}
                layout="stacked"
                align="start"
                contentClassName="gap-3"
              />
              <ul className="mt-6 grid gap-3 text-sm text-text-primary/75">
                {contactPageContent.body.highlights.map((item) => (
                  <li key={item} className="flex gap-3">
                    <span className="mt-1.5 h-2 w-2 flex-none rounded-full bg-brand-accent" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 rounded-2xl bg-surface-cream p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                  {contactPageContent.body.responseTimeLabel}
                </p>
                <p className="mt-2 text-sm font-semibold text-text-primary">
                  {contactPageContent.body.responseTimeValue}
                </p>
              </div>
            </article>

            <article className="rounded-3xl border border-surface-subtle bg-white p-6 shadow-card md:p-8">
              <div className="flex flex-col gap-2">
                <h2 className="text-2xl font-semibold text-brand-primary">
                  {contactPageContent.details.title}
                </h2>
                <p className="text-sm text-text-primary/70">
                  {contactPageContent.details.description}
                </p>
              </div>
              <dl className="mt-6 grid gap-4 sm:grid-cols-2">
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                    {sharedContactLabels?.addressLabel ?? 'Address'}
                  </dt>
                  <dd className="mt-2 text-base font-medium text-text-primary">
                    {contactInfo.addressLines.map((line) => (
                      <div key={line}>{line}</div>
                    ))}
                  </dd>
                </div>
                <div className="flex flex-col gap-4">
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                      {sharedContactLabels?.phoneLabel ?? 'Phone'}
                    </dt>
                    <dd className="mt-2 text-base font-medium text-brand-primary">
                      <a href={phoneLink} className="hover:underline">
                        {contactPhone}
                      </a>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                      {sharedContactLabels?.emailLabel ?? 'Email'}
                    </dt>
                    <dd className="mt-2 text-base font-medium text-brand-primary">
                      <a href={emailLink} className="hover:underline">
                        {contactEmail}
                      </a>
                    </dd>
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                    {sharedContactLabels?.hoursLabel ?? 'Hours'}
                  </dt>
                  <dd className="mt-2 grid gap-2 text-sm text-text-primary/80 sm:grid-cols-2">
                    {contactInfo.hours.map((entry) => (
                      <div key={`${entry.label}-${entry.value}`} className="flex flex-col">
                        <span className="font-medium text-text-primary/70">{entry.label}</span>
                        <span>{entry.value}</span>
                      </div>
                    ))}
                  </dd>
                </div>
              </dl>
              <div className="mt-6 flex flex-wrap gap-3">
                <a
                  href={mapsLink}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex min-h-11 items-center justify-center rounded-full border border-brand-primary/20 px-5 py-2 text-sm font-semibold text-brand-primary transition hover:border-brand-primary hover:bg-brand-primary/10"
                >
                  {contactPageContent.body.directionsLabel}
                </a>
                <Link
                  to="/availabilities"
                  className="inline-flex min-h-11 items-center justify-center rounded-full border border-brand-primary/20 px-5 py-2 text-sm font-semibold text-brand-primary transition hover:border-brand-primary hover:bg-brand-primary/10"
                >
                  {contactPageContent.body.availabilitiesLabel}
                </Link>
              </div>
            </article>
          </div>

          <ContactLeadForm content={contactPageContent.form} contactPhone={contactPhone} />
        </div>
      </PublicPageSection>
    </PublicPageLayout>
  );
}
